import { useRouter, type ErrorComponentProps } from '@tanstack/react-router';
import { PageSkeleton } from './skeleton';
import { ErrorState, Forbidden, NotFound } from './state-views';

/** Centres a state card in whatever room the route has: under the shell's header, or alone on a bare page. */
const FRAME = 'mx-auto flex min-h-[60dvh] w-full max-w-(--mbm-layout-max-content-width) items-center justify-center';

/** The router's answer to a URL nothing matches (Figma P01 "Not found · 404"). */
export function NotFoundPage() {
  return (
    <div className={FRAME}>
      <NotFound />
    </div>
  );
}

/**
 * The router's answer to a route that threw while loading or drawing. Our API errors carry a `kind`: a 403 reads as
 * "no access", a 404 as "not found", anything else as "couldn't load" with a retry that asks the router again.
 */
export function RouteErrorPage({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  const kind = (error as { kind?: string } | null)?.kind;
  return (
    <div className={FRAME}>
      {kind === 'forbidden' ? (
        <Forbidden />
      ) : kind === 'not_found' ? (
        <NotFound title="We can’t find that" />
      ) : (
        <ErrorState
          onRetry={() => {
            reset();
            void router.invalidate();
          }}
        />
      )}
    </div>
  );
}

/** Shown after a route has been loading for a moment (TanStack's pending delay): skeletons, never a full-page spinner. */
export function RoutePending() {
  return (
    <div className="mx-auto w-full max-w-(--mbm-layout-max-content-width) p-(--mbm-space-6)">
      <PageSkeleton />
    </div>
  );
}
